// „Zeiten abrechnen“ -> Rechnungsentwurf in emberfibu (#1007, E3b).
// Mockup: ~/Downloads/mockup_1003_e3b_zeiten(.png|_erfolg.png). Nur sichtbar,
// wenn der Server-Schalter EMBERFIBU_RECHNUNGEN an/exklusiv ist; sonst bleibt
// der bisherige Builder (/invoices/new) der einzige Weg.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckSquare,
  ExternalLink,
  Filter,
  Layers,
  Lock,
  RefreshCw,
  Scale,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import { MobileBottomSlots, MobileTopCenter } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarSlot } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import { api, type BuilderEntry, type FibuUebergabe } from '../lib/api'
import { formatCurrency } from '../lib/format'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/abrechnen',
  component: ZeitenAbrechnenPage,
})

/** emberfibu-Markenfarbe als Fremd-Hinweis (Mockup --fibu). */
const FIBU = '#2dd4bf'

function isoTag(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const t = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${t}`
}

function monatsAnfang(): string {
  const d = new Date()
  return isoTag(new Date(d.getFullYear(), d.getMonth(), 1))
}

function monatsEnde(): string {
  const d = new Date()
  return isoTag(new Date(d.getFullYear(), d.getMonth() + 1, 0))
}

/** 495 -> „8:15 h“ */
export function hm(minuten: number): string {
  const h = Math.floor(minuten / 60)
  const m = Math.round(minuten % 60)
  return `${h}:${String(m).padStart(2, '0')} h`
}

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
function tagKurz(iso: string): string {
  const d = new Date(iso)
  return `${WOCHENTAG[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`
}

function datumDe(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

function zeitraumDe(von: string, bis: string): string {
  const [vy, vm, vd] = von.split('-')
  if (von.slice(0, 4) === bis.slice(0, 4)) return `${vd}.${vm}.–${datumDe(bis)}`
  return `${datumDe(`${vy}-${vm}-${vd}`)}–${datumDe(bis)}`
}

function Punkt({ farbe }: { farbe: string | null }) {
  return (
    <span
      aria-hidden
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ background: farbe ?? FIBU }}
    />
  )
}

function Pill({
  children,
  ton = 'neutral',
}: {
  children: React.ReactNode
  ton?: 'neutral' | 'accent' | 'fibu' | 'warn'
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-medium leading-[14px]',
        ton === 'neutral' && 'bg-bg-overlay text-text-secondary',
        ton === 'accent' && 'bg-accent/10 text-accent',
        ton === 'warn' && 'bg-amber-500/10 text-amber-500',
      )}
      style={ton === 'fibu' ? { background: 'rgb(45 212 191 / .12)', color: FIBU } : undefined}
    >
      {children}
    </span>
  )
}

function Karte({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40',
        className,
      )}
    >
      {children}
    </section>
  )
}

function KartenKopf({ children }: { children: React.ReactNode }) {
  return (
    <header className="flex items-center justify-between gap-2 border-b border-border-subtle bg-bg-overlay/30 px-4 py-3 text-[13px] text-text-secondary">
      {children}
    </header>
  )
}

function Hinweis({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border-subtle bg-bg-overlay px-3 py-2.5 text-xs leading-[1.45] text-text-secondary',
        className,
      )}
    >
      {children}
    </div>
  )
}

function Kv({
  links,
  rechts,
  stark = false,
}: {
  links: React.ReactNode
  rechts: React.ReactNode
  stark?: boolean
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 border-t border-border-subtle py-2 first:border-t-0',
        stark ? 'text-[15px] font-semibold text-text-primary' : 'text-[13px]',
      )}
    >
      <span className={stark ? '' : 'text-text-secondary'}>{links}</span>
      <span className="text-right tabular-nums">{rechts}</span>
    </div>
  )
}

function ZeitenAbrechnenPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const topRef = useRef<HTMLDivElement>(null)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const angemeldet = !!meData?.user
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: angemeldet,
  })
  const aktiv = !!status && status.modus !== 'aus'
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
    enabled: angemeldet && aktiv,
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    enabled: angemeldet && aktiv,
  })

  const [customerId, setCustomerId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [from, setFrom] = useState(monatsAnfang)
  const [to, setTo] = useState(monatsEnde)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [firmaId, setFirmaId] = useState('')
  const [firmaGewaehlt, setFirmaGewaehlt] = useState(false)
  const [erfolg, setErfolg] = useState<FibuUebergabe | null>(null)

  const customers = customersData?.customers ?? []
  const kunde = customers.find((c) => c.id === customerId) ?? null
  const projekte = (projectsData?.projects ?? []).filter((p) => p.customerId === customerId)
  const firmen = status?.firmen ?? []

  // Erster Kunde vorauswählen, damit die Seite sofort etwas zeigt.
  useEffect(() => {
    if (!customerId && customers.length > 0) setCustomerId(customers[0]?.id ?? '')
  }, [customers, customerId])

  const zeitenQuery = useQuery({
    queryKey: ['fibu-zeiten', { customerId, projectId, from, to }],
    queryFn: () =>
      api.fibuZeiten({
        customerId,
        projectIds: projectId ? [projectId] : undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: aktiv && !!customerId && !erfolg,
  })
  // Chronologisch wie im Mockup (der Builder liefert neueste zuerst).
  const entries: BuilderEntry[] = [...(zeitenQuery.data?.entries ?? [])].sort(
    (x, y) => new Date(x.startTime).getTime() - new Date(y.startTime).getTime(),
  )
  const offen = zeitenQuery.data?.offen ?? []

  // Neue Liste -> alles auswählen (Mockup: vorausgewählt), ohne Rate nicht.
  const listenSchluessel = entries.map((e) => e.id).join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: nur bei neuer Liste
  useEffect(() => {
    setSelected(new Set(entries.filter((e) => (e.suggestedRateCents ?? 0) > 0).map((e) => e.id)))
  }, [listenSchluessel])

  const ausgewaehlt = entries.filter((e) => selected.has(e.id))
  const ausgewaehlteProjekte = [
    ...new Set(ausgewaehlt.map((e) => e.projectId).filter((x): x is string => !!x)),
  ]

  const zielQuery = useQuery({
    queryKey: ['fibu-ziel', customerId, ausgewaehlteProjekte.slice().sort().join(',')],
    queryFn: () => api.fibuZiel(customerId, ausgewaehlteProjekte),
    enabled: aktiv && !!customerId,
  })
  const ziel = zielQuery.data

  // Vorschlag übernehmen, solange Dennis nicht selbst gewechselt hat.
  useEffect(() => {
    if (firmaGewaehlt) return
    if (ziel?.firmaId) setFirmaId(ziel.firmaId)
    else if (ziel && !ziel.firmaId) setFirmaId('')
  }, [ziel, firmaGewaehlt])

  const firma = firmen.find((f) => f.id === firmaId) ?? null
  const gemerkt = !!ziel?.gemerkt && ziel.firmaId === firmaId

  const idsSortiert = ausgewaehlt
    .map((e) => e.id)
    .sort()
    .join(',')
  const vorschauQuery = useQuery({
    queryKey: ['fibu-vorschau', { customerId, firmaId, idsSortiert, from, to }],
    queryFn: () =>
      api.fibuVorschau({
        customerId,
        firmaId,
        entryIds: ausgewaehlt.map((e) => e.id),
        von: from || null,
        bis: to || null,
      }),
    enabled: aktiv && !!customerId && !!firmaId && ausgewaehlt.length > 0,
    retry: false,
  })
  const vorschau = vorschauQuery.data?.vorschau ?? null

  const uebergeben = useMutation({
    mutationFn: () =>
      api.fibuUebergeben({
        customerId,
        firmaId,
        entryIds: ausgewaehlt.map((e) => e.id),
        von: from || null,
        bis: to || null,
      }),
    onSuccess: (r) => {
      setErfolg(r.uebergabe)
      qc.invalidateQueries({ queryKey: ['fibu-zeiten'] })
      qc.invalidateQueries({ queryKey: ['fibu-ziel'] })
      qc.invalidateQueries({ queryKey: ['invoice-builder-entries'] })
    },
    onError: (e: Error & { code?: string }) => {
      notify.error(e.message)
      // Unklarer Ausgang: die Übergabe steht als „offen“ im Hinweis oben.
      qc.invalidateQueries({ queryKey: ['fibu-zeiten'] })
    },
  })

  const wiederholen = useMutation({
    mutationFn: (id: string) => api.fibuWiederholen(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['fibu-zeiten'] })
      if (r.uebergabe.status !== 'offen') setErfolg(r.uebergabe)
    },
    onError: (e: Error) => {
      notify.error(e.message)
      qc.invalidateQueries({ queryKey: ['fibu-zeiten'] })
    },
  })
  const verwerfen = useMutation({
    mutationFn: (id: string) => api.fibuVerwerfen(id),
    onSuccess: () => {
      notify.success('Übergabe verworfen — die Zeiten sind wieder frei.')
      qc.invalidateQueries({ queryKey: ['fibu-zeiten'] })
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const summeMin = ausgewaehlt.reduce((s, e) => s + e.durationMinutes, 0)

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function alleUmschalten() {
    if (entries.length > 0 && entries.every((e) => selected.has(e.id))) setSelected(new Set())
    else setSelected(new Set(entries.map((e) => e.id)))
  }

  if (meLoading || statusLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (status && status.modus === 'aus') return <Navigate to="/invoices/new" />

  const zurueck = () => {
    if (erfolg) setErfolg(null)
    else navigate({ to: '/invoices' })
  }

  return (
    <div ref={topRef} className="mx-auto w-full max-w-3xl animate-fade-in">
      <MobileTopCenter>
        <MobileBackTitle
          title="Zeiten abrechnen"
          onBack={zurueck}
          backAriaLabel={erfolg ? 'Zurück zu den Zeiten' : 'Zurück zu den Rechnungen'}
        />
      </MobileTopCenter>
      {!erfolg && (
        <MobileBottomSlots>
          <BarSlot
            label="Filter"
            ariaLabel="Zum Filter"
            icon={<Filter className="h-5 w-5" />}
            onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
          />
          <BarSlot
            label="Alle"
            ariaLabel="Alle Einträge auswählen oder abwählen"
            icon={<CheckSquare className="h-5 w-5" />}
            active={entries.length > 0 && entries.every((e) => selected.has(e.id))}
            onClick={alleUmschalten}
          />
        </MobileBottomSlots>
      )}

      {erfolg ? (
        <Erfolg
          u={erfolg}
          kundeName={customers.find((c) => c.id === erfolg.customerId)?.name ?? ''}
          onZurueck={() => {
            setErfolg(null)
            setFirmaGewaehlt(false)
          }}
        />
      ) : (
        <div className="space-y-3.5 pb-2">
          <div className="phone:px-1">
            <p className="text-2xs font-medium uppercase tracking-wider text-text-tertiary">
              Abrechnen
            </p>
            <h1 className="mt-0.5 text-lg font-medium tracking-tight text-text-primary">
              Zeiten abrechnen
            </h1>
          </div>

          {status && !status.bereit && (
            <Hinweis className="border-amber-500/30">
              emberfibu ist noch nicht eingerichtet (Basis-URL oder Firma mit Zugang fehlt). Siehe
              Betriebsdoku „emberfibu-Rechnungsentwürfe“.
            </Hinweis>
          )}

          {/* Filter */}
          <Karte>
            <div className="space-y-3 p-4">
              <div className="space-y-1">
                <Label htmlFor="fibu-kunde">Kunde</Label>
                <Select
                  id="fibu-kunde"
                  className="h-11 text-[15px]"
                  value={customerId}
                  onChange={(e) => {
                    setCustomerId(e.target.value)
                    setProjectId('')
                    setFirmaGewaehlt(false)
                  }}
                >
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="fibu-von">Von</Label>
                  <Input
                    id="fibu-von"
                    type="date"
                    className="h-11 tabular-nums"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fibu-bis">Bis</Label>
                  <Input
                    id="fibu-bis"
                    type="date"
                    className="h-11 tabular-nums"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Pill mit unsichtbarem nativem Select darüber: Touch-Geräte
                    vergrößern Select-Schrift sonst auf 16 px. */}
                <label className="relative inline-flex">
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent/10 py-0.5 pl-2 pr-1.5 text-2xs font-medium leading-[14px] text-accent">
                    {projekte.find((p) => p.id === projectId)?.name ?? 'Alle Projekte'}
                    <span aria-hidden className="text-[8px]">
                      ▼
                    </span>
                  </span>
                  <select
                    aria-label="Projekt"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  >
                    <option value="">Alle Projekte</option>
                    {projekte.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                <Pill>Nur abrechenbar</Pill>
                <Pill>Bereits abgerechnete ausgeblendet</Pill>
              </div>
            </div>
          </Karte>

          {/* Ziel in emberfibu */}
          <Karte>
            <KartenKopf>
              <span className="flex items-center gap-2">
                <Scale className="h-4 w-4" style={{ color: FIBU }} /> Ziel in emberfibu
              </span>
              {gemerkt && <Pill ton="fibu">gemerkt</Pill>}
            </KartenKopf>
            <div className="space-y-3 p-4">
              <div className="grid grid-cols-[1fr_24px_1fr] items-center gap-1.5">
                <div className="min-w-0 rounded-[10px] border border-border-default bg-bg-overlay/50 px-2.5 py-2">
                  <div className="text-2xs uppercase tracking-wider text-text-tertiary">
                    Kunde · Projekt
                  </div>
                  <div className="mt-0.5 truncate text-[13px] font-medium text-text-primary">
                    {kunde?.name ?? '—'}
                  </div>
                  <div className="mt-0.5 text-xs text-text-tertiary">
                    {ausgewaehlteProjekte.length > 0
                      ? ausgewaehlteProjekte
                          .map((id) => projekte.find((p) => p.id === id)?.name ?? '')
                          .filter(Boolean)
                          .join(', ')
                      : 'keine Zeiten gewählt'}
                  </div>
                </div>
                <ArrowRight className="mx-auto h-4 w-4 text-text-tertiary" aria-hidden />
                <div
                  className="min-w-0 rounded-[10px] border bg-bg-overlay/50 px-2.5 py-2"
                  style={{ borderColor: firma ? 'rgb(45 212 191 / .35)' : undefined }}
                >
                  <div className="text-2xs uppercase tracking-wider text-text-tertiary">Firma</div>
                  <div className="mt-0.5 flex items-center gap-1.5 truncate text-[13px] font-medium text-text-primary">
                    {firma ? (
                      <>
                        <Punkt farbe={firma.farbe} />
                        <span className="truncate">{firma.name}</span>
                      </>
                    ) : (
                      <span className="text-text-tertiary">bitte wählen</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-text-tertiary">
                    {kunde ? `Kontakt: ${kunde.name} · ${kunde.kundennummer}` : ''}
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="fibu-firma">Firma wechseln</Label>
                <Select
                  id="fibu-firma"
                  className="h-11 text-[15px]"
                  value={firmaId}
                  onChange={(e) => {
                    setFirmaId(e.target.value)
                    setFirmaGewaehlt(true)
                  }}
                >
                  <option value="">Firma wählen …</option>
                  {firmen.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </div>
              <Hinweis>
                Nur Firmen, für die Du in emberfibu schreiben darfst. Die Wahl wird für diesen
                Kunden gemerkt; je Projekt abweichend in den Projekteinstellungen.
              </Hinweis>
            </div>
          </Karte>

          {offen.length > 0 && (
            <Karte className="border-amber-500/30">
              <div className="flex gap-2.5 p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                <div className="min-w-0 flex-1 space-y-2 text-xs leading-normal text-text-secondary">
                  {offen.map((u) => (
                    <div key={u.id} className="space-y-2">
                      <p>
                        <b className="text-text-primary">Übergabe an {u.firma.name} unklar</b> (
                        {u.zeiten} Zeiten). Ob der Entwurf in emberfibu angekommen ist, weiß
                        Embertime nicht — die Zeiten bleiben gesperrt. „Erneut senden“ schickt
                        dieselbe Anfrage (kein Doppel).
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => wiederholen.mutate(u.id)}
                          disabled={wiederholen.isPending}
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Erneut senden
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => verwerfen.mutate(u.id)}
                          disabled={verwerfen.isPending}
                        >
                          Verwerfen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Karte>
          )}

          {/* Einträge */}
          <Karte>
            <KartenKopf>
              <span className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                {zeitenQuery.isLoading
                  ? 'Lade …'
                  : entries.length === 1
                    ? '1 Eintrag'
                    : `${entries.length} Einträge`}
              </span>
              <span>
                Auswahl <b className="text-text-primary">{ausgewaehlt.length}</b> ·{' '}
                <b className="tabular-nums text-text-primary">{hm(summeMin)}</b>
              </span>
            </KartenKopf>
            {entries.length === 0 && !zeitenQuery.isLoading ? (
              <p className="p-6 text-center text-sm text-text-secondary">
                Keine offenen abrechenbaren Zeiten im gewählten Zeitraum.
              </p>
            ) : (
              <ul>
                {entries.map((e) => {
                  const an = selected.has(e.id)
                  const betrag = e.suggestedRateCents
                    ? Math.round((e.durationMinutes / 60) * e.suggestedRateCents)
                    : null
                  return (
                    <li key={e.id} className="border-t border-border-subtle first:border-t-0">
                      <label className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-bg-overlay/30">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={an}
                          onChange={() => toggle(e.id)}
                        />
                        <span
                          aria-hidden
                          className={cn(
                            'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded border-[1.6px] peer-focus-visible:ring-2 peer-focus-visible:ring-accent/40',
                            an ? 'border-accent bg-accent text-bg-base' : 'border-text-tertiary',
                          )}
                        >
                          {an && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-text-primary">
                            {e.description}
                          </span>
                          <span className="mt-0.5 block text-xs text-text-tertiary">
                            {tagKurz(e.startTime)} · {e.projectName ?? '—'} · {e.userName ?? '—'}
                          </span>
                        </span>
                        <span className="text-right">
                          <span className="block text-[13px] font-medium tabular-nums text-text-primary">
                            {hm(e.durationMinutes)}
                          </span>
                          <span className="mt-0.5 block text-xs tabular-nums text-text-tertiary">
                            {betrag != null ? formatCurrency(betrag) : '—'}
                          </span>
                        </span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            )}
          </Karte>

          {/* Vorschau pro Projekt — vom Server berechnet (gleiche Regeln wie der Entwurf) */}
          {ausgewaehlt.length > 0 && (
            <Karte>
              <KartenKopf>
                <span>Vorschau Entwurf</span>
                <Pill>pro Projekt</Pill>
              </KartenKopf>
              <div className="p-4">
                {!firmaId ? (
                  <p className="text-sm text-text-secondary">Bitte zuerst die Firma wählen.</p>
                ) : vorschauQuery.isError ? (
                  <p className="text-sm text-red-400">{(vorschauQuery.error as Error).message}</p>
                ) : !vorschau ? (
                  <p className="text-sm text-text-secondary">Berechne …</p>
                ) : (
                  <>
                    {vorschau.positionen.map((p) => (
                      <Kv
                        key={`${p.projektId}-${p.einzelpreisNettoCent}`}
                        links={`${p.bezeichnung} · ${hm(p.minuten)} × ${formatCurrency(p.einzelpreisNettoCent)}`}
                        rechts={formatCurrency(p.nettoCent)}
                      />
                    ))}
                    <Kv links="Netto" rechts={formatCurrency(vorschau.nettoCent)} />
                    <Kv
                      links={
                        vorschau.steuersatzProzent === 0
                          ? 'ohne USt (Kleinunternehmer)'
                          : `zzgl. ${vorschau.steuersatzProzent} % USt`
                      }
                      rechts={formatCurrency(vorschau.ustCent)}
                    />
                    <Kv stark links="Brutto" rechts={formatCurrency(vorschau.bruttoCent)} />
                    <Hinweis className="mt-2.5">
                      Leistungszeitraum{' '}
                      {zeitraumDe(vorschau.leistungszeitraum.von, vorschau.leistungszeitraum.bis)}.
                      Die Rechnungsnummer vergibt emberfibu erst beim Festschreiben; Nummernkreis,
                      Absender und Bank kommen aus den Rechnungseinstellungen von{' '}
                      {vorschau.firma.name}.
                    </Hinweis>
                  </>
                )}
              </div>
            </Karte>
          )}

          {/* Aktion (am Phone über der Bottom-Bar klebend) */}
          <div className="sticky bottom-0 z-10 -mx-3 border-t border-border-subtle bg-bg-elevated px-4 py-2.5 sm:mx-0 sm:rounded-2xl sm:border">
            <Button
              size="lg"
              className="h-12 w-full text-[15px]"
              onClick={() => uebergeben.mutate()}
              disabled={!vorschau || uebergeben.isPending || !status?.bereit}
            >
              <Scale className="h-4 w-4" />
              {uebergeben.isPending ? 'Lege Entwurf an …' : 'Entwurf in Buchhaltung anlegen'}
            </Button>
            <p className="mt-1.5 text-center text-xs text-text-tertiary">
              {vorschau
                ? `${vorschau.zeiten} Zeiten · ${formatCurrency(vorschau.nettoCent)} netto → ${vorschau.firma.name}`
                : ausgewaehlt.length === 0
                  ? 'Keine Zeiten ausgewählt'
                  : 'Firma wählen'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function statusText(u: FibuUebergabe): string {
  if (u.status === 'abgerechnet')
    return u.fibuNummer ? `Festgeschrieben · ${u.fibuNummer}` : 'Festgeschrieben'
  if (u.status === 'storniert') return 'Storniert'
  if (u.status === 'freigegeben') return 'Entwurf gelöscht'
  return 'Entwurf · ohne Nummer'
}

function Erfolg({
  u,
  kundeName,
  onZurueck,
}: {
  u: FibuUebergabe
  kundeName: string
  onZurueck: () => void
}) {
  return (
    <div className="pb-4">
      <div className="px-2 pb-2 pt-7 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/10 text-emerald-500">
          <Check className="h-8 w-8" strokeWidth={2.5} />
        </div>
        <h1 className="mt-3.5 text-lg font-medium text-text-primary">Entwurf angelegt</h1>
        <p className="mt-1.5 text-sm leading-normal text-text-secondary">
          Die Rechnung liegt als Entwurf in der Buchhaltung von{' '}
          <b className="text-text-primary">{u.firma.name}</b> und wartet auf Deine Prüfung.
        </p>
      </div>
      <Karte className="mt-4 px-4 py-2">
        <Kv links="Kunde" rechts={kundeName || u.kundennummer} />
        <Kv
          links="Firma"
          rechts={
            <span className="inline-flex items-center gap-1.5">
              <Punkt farbe={u.firma.farbe} />
              {u.firma.name}
            </span>
          }
        />
        <Kv links="Positionen" rechts={`${u.positionen} (pro Projekt)`} />
        <Kv
          links="Betrag"
          rechts={`${formatCurrency(u.nettoCent ?? 0)} netto · ${formatCurrency(u.bruttoCent ?? 0)} brutto`}
        />
        <Kv links="Status" rechts={<Pill ton="fibu">{statusText(u)}</Pill>} />
      </Karte>
      {u.oeffnenUrl ? (
        <a
          href={u.oeffnenUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-medium text-bg-base"
          style={{ background: FIBU, boxShadow: '0 4px 16px -4px rgb(45 212 191 / .4)' }}
        >
          In emberfibu öffnen <ExternalLink className="h-4 w-4" />
        </a>
      ) : null}
      <Button
        variant="secondary"
        size="lg"
        className="mt-2.5 h-12 w-full text-[15px]"
        onClick={onZurueck}
      >
        Zurück zu den Zeiten
      </Button>
      <Karte className="mt-4 flex gap-2.5 p-4">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <p className="text-xs leading-normal text-text-secondary">
          <b className="text-text-primary">{u.zeiten} Zeiten sind jetzt „in Abrechnung“</b> und
          tauchen hier nicht mehr zur Auswahl auf. Wird der Entwurf in emberfibu gelöscht, werden
          sie wieder frei; beim Festschreiben gelten sie als abgerechnet.
        </p>
      </Karte>
      <p className="mt-3 text-center text-xs text-text-tertiary">
        <Link to="/invoices" className="hover:text-accent">
          Zur Rechnungsübersicht
        </Link>
      </p>
    </div>
  )
}
